import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import StatusField from "../../../components/StatusField";
import UpdatedByField from "../../../components/UpdatedByField";
import { uniq } from "lodash";
import { useDataProvider } from "react-admin";
import CustomPriceField from "../../../components/CustomPriceField";
import { StyledButton } from "../../../components/ListShowButton";

interface ISubcontractorAdhocFeeWithType extends ISubcontractorAdhocFee {
  type: "old" | "new";
}

interface ISubcontractorTimesheetAdhocFeeTable {
  data: ISubcontractorAdhocFeeWithType[];
}

const SubcontractorTimesheetAdhocFeeTable = ({ data }: ISubcontractorTimesheetAdhocFeeTable) => {
  const provider = useDataProvider();

  const [subcontractors, setSubcontractors] = useState<Record<number, any>>({});
  const [schools, setSchools] = useState<Record<number, any>>({});

  // Get referenced data
  useEffect(() => {
    if (data && data.length > 0) {
      const subcontractorIds = data
        .map((record: any) => record.subcontractorId)
        .filter((item) => item);
      const schoolIds = data
        .map((record: any) => record.schoolId)
        .filter((item) => item);

      // Extract unique subcontractorId and schoolId
      const uniqueSubcontractorIds = uniq(subcontractorIds);
      const uniqueSchoolIds = uniq(schoolIds);

      // get subcontractors base on uniqueSubcontractorIds,
      // then convert it to dictionary {id: record}
      if (uniqueSubcontractorIds.length > 0) {
        provider
          .getMany('subcontractors', { ids: uniqueSubcontractorIds })
          .then((result) => {
            if (result) {
              const subcontractors = Object.fromEntries(
                result.data.map(item => [item.id, item])
              );

              setSubcontractors(subcontractors);
            }
          });
      }

      // get subcontractors base on uniqueSchoolIds,
      // then convert it to dictionary {id: record}
      if (uniqueSchoolIds.length > 0) {
        provider
          .getMany('schools', { ids: uniqueSchoolIds })
          .then((result) => {
            if (result) {
              const schools = Object.fromEntries(
                result.data.map(item => [item.id, item])
              );

              setSchools(schools);
            }
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const goToSubcontractorFinance = (subcontractorId: number) => {
    const url = `/#/subcontractors/${subcontractorId}/show/2`;
    window.open(url, "_blank");
  };

  // Show nothing if no data
  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <TableContainer component={Paper} style={{ marginTop: "10px" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Adhoc Fee Id</TableCell>
            <TableCell>Subcontractor Identifier</TableCell>
            <TableCell>Subcontractor MBC Code</TableCell>
            <TableCell>Subcontractor</TableCell>
            <TableCell>School</TableCell>
            <TableCell>Route Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Period</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Updated By</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={row.id || index}>
              <TableCell>{row.type === "old" ? "Old Subcontractor Adhoc Fee" : "New Subcontractor Adhoc Fee" }</TableCell>
              <TableCell>{row.id}</TableCell>
              <TableCell>
                {row.subcontractorId && subcontractors[row.subcontractorId]
                  ? subcontractors[row.subcontractorId]?.subcontractorIdentifier || "-"
                  : <LinearProgress style={{ width: '100%' }} />}
              </TableCell>
              <TableCell>
                {row.subcontractorId && subcontractors[row.subcontractorId]
                  ? subcontractors[row.subcontractorId]?.mbcVendorId || "-"
                  : <LinearProgress style={{ width: '100%' }} />}
              </TableCell>
              <TableCell>
                {row.subcontractorId && subcontractors[row.subcontractorId]
                  ? subcontractors[row.subcontractorId]?.name || "-"
                  : <LinearProgress style={{ width: '100%' }} />}
              </TableCell>
              <TableCell>
                {row.schoolId && schools[row.schoolId]
                  ? schools[row.schoolId]?.name || "-"
                  : <LinearProgress style={{ width: '100%' }} />}
              </TableCell>
              <TableCell>{row.routeName || "-"}</TableCell>
              <TableCell>{row.category || "-"}</TableCell>
              <TableCell>{row.period}</TableCell>
              <TableCell>
                <CustomPriceField record={row} source="amount" />
              </TableCell>
              <TableCell>{row.remarks || "-"}</TableCell>
              <TableCell>
                <StatusField record={row} source="status" />
              </TableCell>
              <TableCell>
                {row.updatedBy ? <UpdatedByField record={row} data={row.updatedBy} /> : "-"}
              </TableCell>
              <TableCell>
                <StyledButton
                  variant="outlined"
                  size="small"
                  style={{ color: "#2a9df4", borderColor: "#2a9df4" }}
                  onClick={() =>
                    goToSubcontractorFinance(
                      Number(row.subcontractorId),
                    )
                  }
                >
                  Go to Subcontractor Finance
                </StyledButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SubcontractorTimesheetAdhocFeeTable;